// =============================================================================
// textScale.js - per-page text size, per device.
//
// Any page a user finds hard to read can be enlarged (or shrunk) on its own,
// without affecting other pages. The setting is remembered per page, per
// browser: the right size depends on the screen as much as the person, so a
// laptop and a big office monitor keep separate choices. Stored like the theme
// preference (localStorage), so it applies before any network call.
//
// Pages are keyed by path with record ids replaced, so a size chosen on one
// patient's chart applies to every chart.
//
// The scale is published as the CSS variable --pos-text-scale on <html>.
// Layout zooms the routed page by it, and ui.jsx <Modal> zooms dialogs by it,
// so a dialog opened from an enlarged page matches the page. CSS zoom (not
// transform) keeps layout proportional and does not create a containing block
// for position:fixed children.
// =============================================================================
export const TEXT_SCALE_STEPS = [0.9, 1, 1.1, 1.2, 1.35];
const KEY = "pos-text-scale";

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

export function screenKey(pathname) {
  const p = String(pathname || "/")
    .replace(UUID, ":id")
    .replace(/\/\d+(?=\/|$)/g, "/:id")
    .replace(/\/+$/, "");
  return p || "/";
}

function readAll() {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || "{}");
    return v && typeof v === "object" ? v : {};
  } catch {
    return {};
  }
}

export function getTextScale(key) {
  const v = Number(readAll()[key]);
  return TEXT_SCALE_STEPS.includes(v) ? v : 1;
}

export function applyTextScale(v) {
  if (typeof document === "undefined") return;
  document.documentElement.style.setProperty("--pos-text-scale", String(TEXT_SCALE_STEPS.includes(v) ? v : 1));
}

export function setTextScale(key, v) {
  const next = TEXT_SCALE_STEPS.includes(v) ? v : 1;
  try {
    const all = readAll();
    if (next === 1) delete all[key]; else all[key] = next;
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch { /* private mode: applies for this visit only */ }
  applyTextScale(next);
  return next;
}

export function stepTextScale(key, dir) {
  const i = TEXT_SCALE_STEPS.indexOf(getTextScale(key));
  const j = Math.max(0, Math.min(TEXT_SCALE_STEPS.length - 1, i + (dir > 0 ? 1 : -1)));
  return setTextScale(key, TEXT_SCALE_STEPS[j]);
}
